import * as timeUtil from '@/utils/dateTimeUtil'

const emergencyActionMeta = {
  stop_moving: { variant: 'action-stop', icon: '!' },
  stop_moving_recover: { variant: 'action-recover', icon: '&#10003;' }
}

const alertInfoTypeMeta = {
  1: { labelKey: 'alertInfoObstacleAvoidanceFailed', variant: 'action-stop', icon: '!' },
  2: { labelKey: 'alertInfoBehaviorProhibitionFailed', variant: 'action-stop', icon: '!' }
}

const escapeHtml = value => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const createDetailRow = (label, content, valueClass, rowClass) => `
  <div class="emergency-detail-row ${rowClass}">
    <span class="emergency-detail-label">${escapeHtml(label)}</span>
    <span class="emergency-detail-value ${valueClass}">${content || '<span class="emergency-detail-empty">&mdash;</span>'}</span>
  </div>`

const createBadge = (label, meta) => `
  <span class="emergency-action-badge emergency-${meta.variant}" title="${escapeHtml(label)}">
    <span class="emergency-action-icon" aria-hidden="true">${meta.icon}</span>
    <span>${escapeHtml(label)}</span>
  </span>`

const translateValue = (translate, key, fallback = '') => {
  if (!key) return fallback
  const translated = translate(key)
  return translated === undefined || translated === null || translated === key
    ? fallback || key
    : translated
}

const formatDataTime = value => (
  value !== undefined && value !== null && value !== ''
    ? timeUtil.formatTime(value)
    : ''
)

const formatNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number.toLocaleString() : '0'
}

const formatBytes = value => {
  const bytes = Number(value || 0)
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const amount = bytes / Math.pow(1024, index)
  return (amount >= 10 || index === 0 ? amount.toFixed(0) : amount.toFixed(1)) + ' ' + units[index]
}

const formatDuration = value => {
  const milliseconds = Number(value)
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '--'
  if (milliseconds < 1000) return milliseconds + ' ms'
  if (milliseconds < 60000) return (milliseconds / 1000).toFixed(1) + ' s'
  return Math.floor(milliseconds / 60000) + ' m ' + Math.floor((milliseconds % 60000) / 1000) + ' s'
}

const capacityTriggerKeys = {
  SCHEDULED: 'notifyTriggerScheduled',
  THRESHOLD: 'notifyTriggerThreshold',
  MANUAL: 'notifyTriggerManual'
}

const createCapacityCell = (label, value, tone = 'tone-muted') => `
  <div class="capacity-alert-cell ${tone}">
    <span>${escapeHtml(label)}</span>
    <strong>${escapeHtml(value)}</strong>
  </div>`

/**
 * 容量告警详情：突出磁盘使用率，其余清理结果以紧凑网格展示
 * content 字段结构与 sys_notification.content_json 一致
 */
export const buildCapacityAlertDescription = (summary, content, translate) => {
  let report = {}
  if (content && typeof content === 'object') {
    report = content
  } else if (typeof content === 'string' && content) {
    try {
      report = JSON.parse(content) || {}
    } catch (error) {
      report = {}
    }
  }

  const blocks = []

  if (summary) {
    blocks.push(`<p class="capacity-alert-summary">${escapeHtml(summary)}</p>`)
  }

  const percent = Number(report.diskUsagePercent)
  if (Number.isFinite(percent)) {
    const clamped = Math.min(100, Math.max(0, percent))
    const level = clamped >= 90 ? 'is-danger' : (clamped >= 75 ? 'is-warning' : 'is-normal')
    blocks.push(`
      <div class="capacity-alert-gauge">
        <div class="capacity-alert-gauge-head">
          <span>${escapeHtml(translate('notifyDiskUsageRate'))}</span>
          <strong class="${level}">${clamped.toFixed(1)}%</strong>
        </div>
        <div class="capacity-alert-track"><i class="${level}" style="width:${clamped}%"></i></div>
      </div>`)
  }

  // 语义配色与数据清理页保持一致：青=数据量，绿=回收收益，琥珀=耗时，靛蓝=属性
  const cells = []
  if (report.diskAfterMb !== undefined && report.diskAfterMb !== null) {
    cells.push(createCapacityCell(translate('notifyDiskAfter'), formatNumber(report.diskAfterMb) + ' MB', 'tone-primary'))
  }
  if (report.totalDeleted !== undefined && report.totalDeleted !== null) {
    cells.push(createCapacityCell(translate('notifyDeletedRecords'), formatNumber(report.totalDeleted), 'tone-primary'))
  }
  if (report.freedBytes !== undefined && report.freedBytes !== null) {
    cells.push(createCapacityCell(translate('notifyFreedSpace'), formatBytes(report.freedBytes), 'tone-success'))
  }
  if (report.durationMs !== undefined && report.durationMs !== null) {
    cells.push(createCapacityCell(translate('notifyDuration'), formatDuration(report.durationMs), 'tone-warning'))
  }
  if (report.triggerType) {
    const triggerKey = capacityTriggerKeys[report.triggerType]
    cells.push(createCapacityCell(
      translate('notifyTrigger'),
      triggerKey ? translate(triggerKey) : report.triggerType,
      'tone-info'
    ))
  }
  if (report.phase2Executed !== undefined && report.phase2Executed !== null) {
    const triggered = report.phase2Executed === true || report.phase2Executed === 1
    cells.push(createCapacityCell(
      translate('notifyCapacityGuard'),
      translate(triggered ? 'notifyTriggered' : 'notifyNotTriggered'),
      triggered ? 'tone-success' : 'tone-muted'
    ))
  }

  if (cells.length) {
    blocks.push(`<div class="capacity-alert-grid">${cells.join('')}</div>`)
  }

  return blocks.join('')
}

export const buildNotificationContent = (sourceType, message, translate) => {
  const payload = message.data || {}
  const robotName = message.robotName || ''
  const robotCode = message.robotCode || ''
  const rows = [
    createDetailRow(translate('robot'), escapeHtml(robotName), 'emergency-value-robot', 'emergency-row-robot'),
    createDetailRow(translate('dashboard_robotCode'), escapeHtml(robotCode), 'emergency-value-code', 'emergency-row-code')
  ]

  let titleKey = 'emergencyNotificationTitle'

  if (sourceType === 'alert_info') {
    titleKey = 'alertInfoNotificationTitle'
    const typeMeta = alertInfoTypeMeta[payload.type] || {
      variant: 'action-unknown',
      icon: '?'
    }
    const typeLabel = typeMeta.labelKey
      ? translateValue(translate, typeMeta.labelKey, String(payload.type || ''))
      : String(payload.type || '')

    rows.push(
      createDetailRow(
        translate('alertInfoType'),
        typeLabel ? createBadge(typeLabel, typeMeta) : '',
        'emergency-value-alert',
        'emergency-row-alert'
      )
    )
  } else {
    const action = payload.action || ''
    const reason = payload.reason || ''
    const actionMeta = emergencyActionMeta[action] || {
      variant: 'action-unknown',
      icon: '?'
    }
    const actionLabel = translateValue(translate, action, action)
    const reasonLabel = reason
      ? translateValue(translate, `${action}_disconnection`, reason)
      : ''

    rows.push(
      createDetailRow(
        translate('action'),
        action ? createBadge(actionLabel, actionMeta) : '',
        'emergency-value-action',
        'emergency-row-action'
      ),
      createDetailRow(
        translate('reason'),
        reason ? `<span class="emergency-reason-text">${escapeHtml(reasonLabel)}</span>` : '',
        'emergency-value-reason',
        'emergency-row-reason'
      )
    )
  }

  rows.push(
    createDetailRow(
      translate('dashboard_dataTime'),
      escapeHtml(formatDataTime(payload.dataTime)),
      'emergency-value-time',
      'emergency-row-time'
    )
  )

  return {
    title: translate(titleKey),
    description: rows.join(''),
    robotId: message.robotId || ''
  }
}
